<template>
  <div class="virtual-el-table">
    <el-table
      ref="table"
      :data="visibleData"
      :height="height"
      :default-sort="defaultSort"
      v-bind="$attrs"
      v-on="$listeners"
      @sort-change="onSortChange"
    >
      <slot />
    </el-table>
  </div>
</template>

<script>
const BUFFER = 4

export default {
  name: 'VirtualElTable',
  inheritAttrs: false,
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    height: {
      type: Number,
      default: 400,
    },
    rowHeight: {
      type: Number,
      default: 32,
    },
    defaultSort: {
      type: Object,
      default: () => ({ prop: '', order: '' }),
    },
  },
  data() {
    return {
      scrollTop: 0,
      startIndex: 0,
      endIndex: 0,
      visibleData: [],
      sortProp: this.defaultSort.prop || '',
      sortOrder: this.defaultSort.order || '',
      bodyWrapper: null,
      tableBody: null,
      phantom: null,
    }
  },
  computed: {
    bodyHeight() {
      return Math.max(this.rowHeight, this.height - 40)
    },
    sortedData() {
      if (!this.sortProp || !this.sortOrder) return this.data
      const dir = this.sortOrder === 'ascending' ? 1 : -1
      const prop = this.sortProp
      return [...this.data].sort((a, b) => {
        const av = a[prop]
        const bv = b[prop]
        if (av == null && bv == null) return 0
        if (av == null) return -dir
        if (bv == null) return dir
        if (typeof av === 'number' && typeof bv === 'number') {
          return (av - bv) * dir
        }
        return String(av).localeCompare(String(bv), 'zh-CN') * dir
      })
    },
    totalHeight() {
      return this.sortedData.length * this.rowHeight
    },
    visibleCount() {
      return Math.ceil(this.bodyHeight / this.rowHeight) + 1
    },
  },
  watch: {
    data: {
      handler() {
        this.$nextTick(() => {
          this.ensureVirtualScroll()
          this.resetVirtualScroll()
        })
      },
      deep: false,
    },
    height() {
      this.$nextTick(() => {
        this.relayout()
      })
    },
    defaultSort: {
      handler(val) {
        if (!val) return
        this.sortProp = val.prop || ''
        this.sortOrder = val.order || ''
        this.resetVirtualScroll()
      },
      deep: true,
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.ensureVirtualScroll()
      this.resetVirtualScroll()
    })
  },
  beforeDestroy() {
    this.destroyVirtualScroll()
  },
  methods: {
    getTable() {
      return this.$refs.table
    },
    getBodyWrapper() {
      const table = this.getTable()
      if (!table) return null
      return (
        table.bodyWrapper
        || table.$refs?.bodyWrapper
        || table.$el?.querySelector('.el-table__body-wrapper')
      )
    },
    initVirtualScroll() {
      this.destroyVirtualScroll()

      const bodyWrapper = this.getBodyWrapper()
      const tableBody = bodyWrapper?.querySelector('.el-table__body')
      if (!bodyWrapper || !tableBody) return

      this.bodyWrapper = bodyWrapper
      this.tableBody = tableBody

      bodyWrapper.style.position = 'relative'
      tableBody.style.position = 'absolute'
      tableBody.style.top = '0'
      tableBody.style.left = '0'
      tableBody.style.width = '100%'

      this.phantom = document.createElement('div')
      this.phantom.className = 'virtual-el-table__phantom'
      bodyWrapper.insertBefore(this.phantom, bodyWrapper.firstChild)

      bodyWrapper.addEventListener('scroll', this.onScroll, { passive: true })
    },
    ensureVirtualScroll() {
      if (!this.getBodyWrapper() || !this.phantom?.parentNode) {
        this.initVirtualScroll()
      }
    },
    destroyVirtualScroll() {
      if (this.bodyWrapper) {
        this.bodyWrapper.removeEventListener('scroll', this.onScroll)
      }
      if (this.phantom?.parentNode) {
        this.phantom.parentNode.removeChild(this.phantom)
      }
      if (this.tableBody) {
        this.tableBody.style.position = ''
        this.tableBody.style.top = ''
        this.tableBody.style.left = ''
        this.tableBody.style.width = ''
        this.tableBody.style.transform = ''
      }
      if (this.bodyWrapper) {
        this.bodyWrapper.style.position = ''
      }
      this.bodyWrapper = null
      this.tableBody = null
      this.phantom = null
    },
    updatePhantom() {
      if (this.phantom) {
        this.phantom.style.height = `${this.totalHeight}px`
      }
    },
    onScroll() {
      if (!this.bodyWrapper) return
      this.scrollTop = this.bodyWrapper.scrollTop
      this.updateVisibleData()
    },
    updateVisibleData() {
      const length = this.sortedData.length
      if (!length) {
        this.startIndex = 0
        this.endIndex = 0
        this.visibleData = []
        if (this.tableBody) {
          this.tableBody.style.transform = 'translateY(0px)'
        }
        this.emitRangeChange(0, 0)
        return
      }

      const start = Math.max(0, Math.floor(this.scrollTop / this.rowHeight) - BUFFER)
      const end = Math.min(length, start + this.visibleCount + BUFFER * 2)

      if (this.tableBody) {
        this.tableBody.style.transform = `translateY(${start * this.rowHeight}px)`
      }

      if (start === this.startIndex && end === this.endIndex && this.visibleData.length) {
        return
      }

      this.startIndex = start
      this.endIndex = end
      this.visibleData = this.sortedData.slice(start, end)
      this.emitRangeChange(start, end)
    },
    emitRangeChange(startIndex, endIndex) {
      this.$emit('range-change', { startIndex, endIndex })
    },
    onSortChange({ prop, order }) {
      if (!order) {
        this.sortProp = this.defaultSort.prop || ''
        this.sortOrder = this.defaultSort.order || ''
      } else {
        this.sortProp = prop
        this.sortOrder = order
      }
      this.resetVirtualScroll()
      this.$emit('sort-change', { prop: this.sortProp, order: this.sortOrder })
    },
    resetVirtualScroll() {
      this.ensureVirtualScroll()
      this.scrollTop = 0
      this.startIndex = -1
      this.endIndex = -1
      if (this.bodyWrapper) {
        this.bodyWrapper.scrollTop = 0
      }
      this.updatePhantom()
      this.updateVisibleData()
      this.$nextTick(() => {
        this.getTable()?.doLayout?.()
      })
    },
    relayout() {
      this.ensureVirtualScroll()
      this.getTable()?.doLayout?.()
      this.updatePhantom()
      this.updateVisibleData()
    },
  },
}
</script>

<style scoped>
.virtual-el-table {
  width: 100%;
  min-width: 0;
}

.virtual-el-table >>> .virtual-el-table__phantom {
  width: 100%;
  pointer-events: none;
}
</style>
